import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHowlAudio } from 'components/hooks/useHowlProvider';
import {VolumeUp, VolumeOff, KeyboardArrowDown, KeyboardArrowRight} from '@mui/icons-material';
import { Slider, Menu, MenuItem, Button, ListItemText, Box } from '@mui/material';
import {
  PauseCircleFilledOutlined,
  PlayCircleFilledOutlined,
} from '@mui/icons-material';
import { useBingoThemeSongs } from './useBingoThemeSongs';
import { styled } from '@mui/material/styles';

interface BingoThemeSongItem {
  url: string;
  title: string;
  genres: string[];
}

interface PlayThemeSongProps {
  instanceId?: string;
  initialSongIndex?: number;
  resetKey?: string;
}

const CAPSULE_GENRE = '#1565c0';
const CAPSULE_SONG = '#37474f';
const SONG_SELECTED_BG = '#78909c';
const SONG_SELECTED_BG_HOVER = '#607d8b';
const CAPSULE_TEXT_MUTED = '#616161';

const CapsuleMenuButton = styled(Button)(() => ({
  borderRadius: 9999,
  backgroundColor: '#ffffff',
  border: '1px solid rgba(0, 0, 0, 0.1)',
  textTransform: 'none',
  padding: '10px 18px',
  fontSize: '14px',
  fontWeight: 500,
  color: CAPSULE_SONG,
  justifyContent: 'space-between',
  gap: 10,
  minHeight: 44,
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
  transition: 'box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease',
  '&:hover': {
    backgroundColor: '#ffffff',
    borderColor: 'rgba(0, 0, 0, 0.16)',
    boxShadow: '0 4px 14px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.05)',
    transform: 'translateY(-1px)',
  },
  '&:active': {
    transform: 'translateY(0)',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
  },
  '&.Mui-focusVisible': {
    outline: '2px solid rgba(0, 0, 0, 0.35)',
    outlineOffset: 2,
  },
}));

const GenreMenuItem = styled(MenuItem)(() => ({
  borderRadius: '12px',
  margin: '4px 8px',
  padding: '10px 16px',
  fontSize: '14px',
  color: CAPSULE_GENRE,
  '&:hover': {
    backgroundColor: '#e3f2fd',
    borderRadius: '12px',
  },
  '&.Mui-selected': {
    backgroundColor: CAPSULE_GENRE,
    color: '#ffffff',
    '&:hover': {
      backgroundColor: '#0d47a1',
    },
  },
}));

const SongMenuItem = styled(MenuItem)(() => ({
  borderRadius: '12px',
  margin: '4px 8px',
  padding: '10px 16px',
  fontSize: '14px',
  color: CAPSULE_SONG,
  '&:hover': {
    backgroundColor: 'rgba(55, 71, 79, 0.1)',
    borderRadius: '12px',
  },
  '&.Mui-selected': {
    backgroundColor: SONG_SELECTED_BG,
    color: '#ffffff',
    '&:hover': {
      backgroundColor: SONG_SELECTED_BG_HOVER,
    },
  },
}));

const PlayThemeSong: React.FC<PlayThemeSongProps> = ({instanceId = "bingoThemeSong", initialSongIndex = 0, resetKey = ''}) => {
  const { songs: bingoThemeSongs, genres: genreList } = useBingoThemeSongs();
  const { 
    isHowlPlaying, 
    setHowlVolume, 
    pauseHowl, 
    resumeHowl,   
    getHowlInstance ,
    isPlaying,
    playHowl
  } = useHowlAudio();
 // const isPlaying = useMemo(() => isHowlPlaying("bingoThemeSong"), [isHowlPlaying]);
  const howlId = instanceId;
  const [playThemeSong, setPlayThemeSong] = useState<boolean>(false);
  const [isIOS, setIsIOS] = useState<boolean>(false);
  const [currentVolume, setCurrentVolume] = useState<number>(20);
  const [selectedGenre, setSelectedGenre] = useState<string>('');
  const [selectedSongIndex, setSelectedSongIndex] = useState<number>(0);
  const initGenreSongRef = useRef(false);
  const didAutoplayDefaultRef = useRef(false);
  const [pickerAnchor, setPickerAnchor] = useState<null | HTMLElement>(null);
  const [expandedGenre, setExpandedGenre] = useState<string>('');

  const bingoSongs = bingoThemeSongs as BingoThemeSongItem[];

  const effectiveGenre = selectedGenre || genreList[0] || '';

  const filteredSongs = useMemo(() => {
    if (!effectiveGenre) return [];
    return bingoSongs.filter((s) => (s.genres || []).includes(effectiveGenre));
  }, [bingoSongs, effectiveGenre]);

  useEffect(() => {
    const userAgent = navigator.userAgent;
    if (/iPad|iPhone|iPod/.test(userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) {
      setIsIOS(true);
    }
    setPlayThemeSong(!isPlaying);
    
    const howlInstance = getHowlInstance(howlId);
    if (howlInstance) {
      const volume = howlInstance.volume();
      setCurrentVolume(Math.round(volume * 100));
    }
  }, [getHowlInstance, howlId, isHowlPlaying, isPlaying]);

  useEffect(() => {
    initGenreSongRef.current = false;
    didAutoplayDefaultRef.current = false;
    setSelectedGenre('');
    setSelectedSongIndex(0);
  }, [howlId, resetKey]);

  useEffect(() => {
    if (!bingoSongs.length || !genreList.length) return;
    initGenreSongRef.current = true;

    // Requirement: default sound should be the 1st audio in the 1st genre in the list.
    const g = genreList[0] || '';
    const inGenre = g ? bingoSongs.filter((s) => (s.genres || []).includes(g)) : [];
    const song = inGenre[0] || bingoSongs[0];

    if (g) setSelectedGenre(g);
    setSelectedSongIndex(0);

    if (song?.url) {
      const shouldAutoplay = !didAutoplayDefaultRef.current;
      didAutoplayDefaultRef.current = true;
      loadSongIntoHowl(song, { forcePlay: shouldAutoplay });
    }
  }, [bingoSongs, genreList, resetKey]);

  useEffect(() => {
    if (filteredSongs.length && selectedSongIndex >= filteredSongs.length) {
      setSelectedSongIndex(0);
    }
  }, [filteredSongs.length, selectedSongIndex]);

  useEffect(() => {
    if (!genreList.length) return;
    if (selectedGenre && !genreList.includes(selectedGenre)) {
      setSelectedGenre(genreList[0]);
      setSelectedSongIndex(0);
    }
  }, [genreList, selectedGenre]);

  const handleVolumeChange = (_event: Event, value: number | number[], _activeThumb: number) => {
    const newVolume = Array.isArray(value) ? value[0] : value;
    setCurrentVolume(newVolume);
    
    // Convert percentage to 0-1 range for Howler
    const howlerVolume = newVolume / 100;
    setHowlVolume(howlerVolume, howlId);
    
    if (newVolume >= 1 && newVolume <= 100) {
      setPlayThemeSong(false);
    } else if (newVolume === 0) {
      setPlayThemeSong(true);
    }
  };

  const togglePlayPause = () => {
    const isPlaying = isHowlPlaying(howlId);
    if (isPlaying) {
      pauseHowl(howlId);
      setPlayThemeSong(true);
    } else {
      resumeHowl(howlId);
      setPlayThemeSong(false);
    }
  };

  const handleMuteToggle = () => {
    if (playThemeSong) {
      setHowlVolume(currentVolume / 100, howlId);
      setPlayThemeSong(false);
    } else {
      setHowlVolume(0, howlId);
      setPlayThemeSong(true);
    }
  };

  const loadSongIntoHowl = (
    selectedSong: BingoThemeSongItem,
    options?: { forcePlay?: boolean }
  ) => {
    const forcePlay = Boolean(options?.forcePlay);
    const wasPlaying = isHowlPlaying(howlId);
    const audioConfig = {
      src: [selectedSong.url],
      volume: currentVolume / 100,
      loop: true,
      html5: true,
      preload: true,
      onError: (error: unknown) => {
        console.error("Error playing audio:", error);
      },
      onloaderror: (id: unknown, err: unknown) => {
        console.error("Audio failed to load:", err);
      },
      onplayerror: (id: unknown, err: unknown) => {
        console.error("Audio failed to play:", err);
      },
    };
    playHowl(audioConfig, howlId);
    setTimeout(() => {
      if (!forcePlay && !wasPlaying) {
        pauseHowl(howlId);
        return;
      }
    }, 100);
  };

  const closePicker = () => {
    setPickerAnchor(null);
  };

  const songsForGenre = (g: string) =>
    bingoSongs.filter((s) => (s.genres || []).includes(g));

  const pickSongFromMenu = (genre: string, song: BingoThemeSongItem, indexInGenre: number) => {
    setSelectedGenre(genre);
    setSelectedSongIndex(indexInGenre);
    loadSongIntoHowl(song);
    closePicker();
  };

  const menuPaperSx = useMemo(
    () => ({
      borderRadius: '16px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      border: '1px solid #e0e0e0',
      maxHeight: isIOS ? 'min(320px, 65vh)' : 320,
      ...(isIOS ? { zIndex: 99999 } : {}),
    }),
    [isIOS]
  );

  const currentSongTitle = filteredSongs[selectedSongIndex]?.title ?? 'Song';

  const openPicker = (anchor: HTMLElement) => {
    const g = effectiveGenre || genreList[0] || '';
    setExpandedGenre(g);
    setPickerAnchor(anchor);
  };

  const toggleExpandedGenre = (g: string) => {
    setExpandedGenre((prev) => (prev === g ? '' : g));
  };



  const hasSlider = !isIOS;

  return (
    <div className={`allBingoVolumeSection ${!hasSlider ? "allBingoVolumeSectionNoSlider" : ""}`}>
      <span className="bingoPlayPauseWrap" onClick={togglePlayPause}>
        {isPlaying ? (
          <PauseCircleFilledOutlined
            className="bingoPausIcon"
            style={{ color: "black", cursor: "pointer" }}
          />
        ) : (
          <PlayCircleFilledOutlined
            className="bingoPlayIcon"
            style={{ color: "black", cursor: "pointer" }}
          />
        )}
      </span>
      {!isIOS && (
      playThemeSong ? (
        <VolumeOff
          style={{ color: "black", cursor: "pointer" }}
          onClick={handleMuteToggle}
        />
      ) : (
        <VolumeUp
          style={{ color: "black", cursor: "pointer" }}
          onClick={handleMuteToggle}
        />
      )
    )}
      {!isIOS && (
      <Slider
        className="bingoVolumeSlider"
        defaultValue={70}
        value={currentVolume}
        onChange={handleVolumeChange as (event: Event, value: number | number[], activeThumb: number) => void}
        aria-labelledby="continuous-slider"
        min={0}
        max={100}
        step={1}
        valueLabelDisplay="auto"
        style={{ color: "#1976d2", width: "150px", marginLeft: "12px", touchAction: "none" }}
      />
    )}
      <CapsuleMenuButton
        size="small"
        variant="text"
        disableElevation
        onClick={(e) => openPicker(e.currentTarget)}
        endIcon={
          <KeyboardArrowDown
            sx={{ color: CAPSULE_TEXT_MUTED, flexShrink: 0, fontSize: 22 }}
          />
        }
        sx={{
          marginLeft: hasSlider ? '12px' : '8px',
          minWidth: isIOS ? 168 : 152,
          maxWidth: isIOS ? 260 : 300,
        }}
      >
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textAlign: 'left',
            flex: 1,
            letterSpacing: '0.01em',
          }}
        >
          <span style={{ color: CAPSULE_GENRE, fontWeight: 700 }}>{effectiveGenre || 'Genre'}</span>
          <span style={{ margin: '0 8px', color: CAPSULE_TEXT_MUTED, fontWeight: 600 }}>·</span>
          <span style={{ color: CAPSULE_SONG, fontWeight: 600 }}>{currentSongTitle}</span>
        </span>
      </CapsuleMenuButton>
      <Menu
        anchorEl={pickerAnchor}
        open={Boolean(pickerAnchor)}
        onClose={closePicker}
        disableScrollLock={isIOS}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              ...menuPaperSx,
              mt: 1,
              overflow: 'hidden',
              maxWidth: 'min(96vw, 420px)',
            },
          },
        }}
        MenuListProps={{
          component: 'div',
          sx: { p: 0, display: 'block' },
        }}
        disableAutoFocus
        {...(isIOS && {
          container: typeof document !== 'undefined' ? document.body : undefined,
          disableEnforceFocus: true,
          disableRestoreFocus: true,
        })}
      >
        <Box
          sx={{
            maxHeight: isIOS ? 'min(320px, 60vh)' : 320,
            overflowY: 'auto',
            py: 0.5,
            px: 0.25,
          }}
        >
          {genreList.map((g) => {
            const isExpanded = expandedGenre === g;
            const songsInGenre = songsForGenre(g);
            return (
              <Box key={g} sx={{ mb: 0.25 }}>
                <GenreMenuItem
                  selected={g === effectiveGenre}
                  onClick={() => toggleExpandedGenre(g)}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 0.5,
                    borderRadius: '12px',
                    mx: 0.5,
                    my: 0.25,
                  }}
                >
                  <ListItemText
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: 'inherit',
                    }}
                    primary={g}
                    sx={{ m: 0, color: 'inherit' }}
                  />
                  <KeyboardArrowRight
                    sx={{
                      color: 'inherit',
                      opacity: 0.85,
                      fontSize: 18,
                      flexShrink: 0,
                      transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                      transition: 'transform 0.15s ease',
                    }}
                  />
                </GenreMenuItem>

                {isExpanded && (
                  <Box sx={{ pb: 0.25 }}>
                    {songsInGenre.map((song, indexInGenre) => (
                      <SongMenuItem
                        key={`${song.url}-${indexInGenre}`}
                        selected={
                          g === effectiveGenre &&
                          song.url === filteredSongs[selectedSongIndex]?.url
                        }
                        onClick={() => pickSongFromMenu(g, song, indexInGenre)}
                        sx={{
                          borderRadius: '12px',
                          mx: 0.5,
                          my: 0.25,
                          pl: 3,
                        }}
                      >
                        <ListItemText
                          primaryTypographyProps={{ fontSize: 14, color: 'inherit' }}
                          primary={song.title}
                          sx={{ m: 0, color: 'inherit' }}
                        />
                      </SongMenuItem>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Menu>
    </div>
  );
};

export default PlayThemeSong;

